import { updateIntelligemsStickyPrice } from './based-intelligems-ui.js';

const controllers = new WeakMap();
// A selector row belongs to a scent through a scalar scent, a scent-group
// list, or both. A fallback snapshot can carry scent groups only.
const SCENT_WRAP = '.bbw-pdp-buy__variant-wrap[data-bbw-scent], .bbw-pdp-buy__variant-wrap[data-bbw-scent-groups]';

export function bindRedesignPurchasePresentation(root, { signal, onChange = () => {} } = {}) {
  const productForm = root.querySelector('product-form-component[data-bbw-purchase-lock="true"]');
  const mainControl = root.querySelector('[data-bbw-pdp-atc]');
  const label = root.querySelector('[data-bbw-pdp-cta-label]');
  if (!productForm || !mainControl) return;
  root.addEventListener('based:product-form-add-start', event => {
    if (event.target !== productForm) return;
    mainControl.disabled = true;
    mainControl.classList.add('is-loading');
    mainControl.setAttribute('aria-busy', 'true');
    onChange(true);
  }, { signal });
  root.addEventListener('based:product-form-add-end', event => {
    if (event.target !== productForm) return;
    mainControl.classList.remove('is-loading');
    mainControl.removeAttribute('aria-busy');
    const state = mainControl.getAttribute('data-purchase-state');
    mainControl.disabled = state === 'out_of_stock';
    if (label) label.textContent = mainControl.getAttribute(state === 'out_of_stock' ? 'data-label-oos' : state === 'notify_me' ? 'data-label-notify' : 'data-label-add') || '';
    onChange(false);
  }, { signal });
}
// End redesign purchase presentation.

export function bindRedesignFloatingDelegate(sticky, mainControl, signal) {
  const floatingControl = sticky?.querySelector('[data-bbw-sticky-atc]');
  if (!floatingControl) return;
  floatingControl.addEventListener('click', () => {
    const control = mainControl || document.getElementById(floatingControl.getAttribute('data-bbw-pdp-delegates-to'));
    if (control && !control.disabled) control.click();
  }, { signal });
}
// End redesign floating delegate.

export function bindPrice(container, detail, data) {
  if (!container) return;
  container.setAttribute('data-product-id', detail.productId);
  container.setAttribute('data-variant-id', detail.variantId);
  updateIntelligemsStickyPrice(container, detail);
  const now = container.querySelector('.igPrice');
  const was = container.querySelector('.igComparePrice');
  if (now) {
    now.setAttribute('data-variant-id', detail.variantId);
    now.setAttribute('data-price', data.price || 0);
    now.setAttribute('data-price-formatted', detail.formattedPrice || '');
  }
  if (was) {
    was.setAttribute('data-variant-id', detail.variantId);
    was.setAttribute('data-compare-at-price', data.compareAtPrice || 0);
    was.setAttribute('data-compare-at-price-formatted', detail.formattedComparePrice || '');
    was.hidden = false;
  }
}

/** One section owns its listeners, observers, timers, and relocated overlays. */
export function initializeBuyBox(root) {
  if (controllers.has(root)) return controllers.get(root);
  const abort = new AbortController();
  const cleanups = [];
  const on = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal: abort.signal });
  const cleanup = callback => cleanups.push(callback);
  const observe = (observer, target) => { observer.observe(target); cleanup(() => observer.disconnect()); };
  const gallery = bindGalleryAndTips(root, { on, observe, cleanup });
  const form = root.querySelector('form');
  const idInput = root.querySelector('[data-bbw-pdp-id]');
  const main = root.querySelector('[data-bbw-pdp-atc]');
  const rows = Array.from(root.querySelectorAll('button[data-bbw-pdp-variant]'));
  const scents = Array.from(root.querySelectorAll('[data-bbw-scent-val]'));
  const sticky = Array.from(document.querySelectorAll('[data-bbw-sticky]')).find(node => node.dataset.sectionId === root.dataset.sectionId);
  const floating = sticky?.querySelector('[data-bbw-sticky-atc]');
  const stickyParent = sticky?.parentNode;
  const stickySibling = sticky?.nextSibling;
  let selected;
  let data;
  const enabled = node => !node.disabled && node.getAttribute('aria-disabled') !== 'true';
  let pending = main?.getAttribute('aria-busy') === 'true';
  let scentTimer;

  function syncControls() {
    const state = data?.purchaseState || main?.dataset.purchaseState || 'out_of_stock';
    for (const control of [main, floating]) {
      if (!control) continue;
      control.dataset.purchaseState = state;
      control.disabled = pending || state === 'out_of_stock';
      control.classList.toggle('is-oos', state === 'out_of_stock');
      control.classList.toggle('is-loading', pending);
      if (pending) control.setAttribute('aria-busy', 'true');
      else control.removeAttribute('aria-busy');
      const label = state === 'out_of_stock' ? main?.dataset.labelOos : state === 'notify_me' ? main?.dataset.labelNotify : main?.dataset.labelAdd;
      const text = control.querySelector('[data-bbw-pdp-cta-label], .bbw-fab__text');
      if (text) text.textContent = label || '';
      else if (control === floating) control.textContent = label || '';
    }
    if (main) {
      main.type = state === 'notify_me' ? 'button' : 'submit';
      main.classList.toggle('based-klaviyo-notify-trigger', state === 'notify_me');
    }
    measureFab();
  }

  function measureFab() {
    if (!sticky?.hasAttribute('data-bbw-sticky-fab') || !floating) return;
    sticky.classList.add('is-measuring');
    const width = Math.ceil(floating.getBoundingClientRect().width) + 52;
    const labelWidth = Math.ceil(floating.querySelector('.bbw-fab__label')?.getBoundingClientRect().width || 0);
    sticky.classList.remove('is-measuring');
    if (width > 52) sticky.style.setProperty('--fab-w', `${width}px`);
    if (labelWidth) sticky.style.setProperty('--fab-label-w', `${labelWidth}px`);
  }

  function moveScentIndicator() {
    if (abort.signal.aborted) return;
    const track = root.querySelector('[data-bbw-scent-track]');
    const indicator = root.querySelector('[data-bbw-scent-ind]');
    const active = scents.find(node => node.getAttribute('aria-checked') === 'true');
    if (track && indicator && active) {
      indicator.style.width = `${active.offsetWidth}px`;
      indicator.style.transform = `translateX(${active.offsetLeft}px)`;
      indicator.style.borderColor = getComputedStyle(active).getPropertyValue('--scent-c').trim();
      track.classList.add('is-animated');
    }
    measureFab();
  }

  function syncScent(value) {
    if (!value) return;
    root.querySelectorAll(SCENT_WRAP).forEach(wrap => { wrap.hidden = !rowMatchesScent(wrap, value); });
    scents.forEach(node => {
      const active = node.dataset.bbwScentVal === value;
      node.setAttribute('aria-checked', String(active));
      node.setAttribute('aria-current', String(active));
      node.tabIndex = active ? 0 : -1;
    });
    moveScentIndicator();
  }

  function select(row, initial = false, requestedScent = '') {
    if (!row) return;
    const payload = row.getAttribute('data-bbw-pdp-variant-payload');
    if (!payload) return;
    data = JSON.parse(payload);
    selected = row;
    const description = root.querySelector('[data-bbw-scent-description]');
    const scentPrefix = root.querySelector('[data-bbw-scent-prefix]');
    const scentCopy = root.querySelector('[data-bbw-scent-copy]');
    if (scentPrefix) {
      scentPrefix.textContent = Number(data.scentCount) > 1
        ? scentPrefix.dataset.labelPlural
        : scentPrefix.dataset.labelSingular;
    }
    if (scentCopy) scentCopy.textContent = data.scentCopy || '';
    if (description) description.hidden = !data.scentCopy?.trim();
    rows.forEach(other => other.setAttribute('aria-checked', String(other === row)));
    if (idInput) idInput.value = String(data.variantId);
    if (form) {
      form.dataset.purchaseState = data.purchaseState;
      form.dataset.basedDirectCheckoutUrl = data.directCheckoutUrl || '';
    }
    if (main) {
      if (data.directCheckoutUrl) { main.setAttribute('formaction', data.directCheckoutUrl); main.setAttribute('formmethod', 'get'); }
      else { main.removeAttribute('formaction'); main.removeAttribute('formmethod'); }
    }
    const selectedWrap = row.closest(SCENT_WRAP);
    const activeScent = scents.find(node => node.getAttribute('aria-checked') === 'true')?.dataset.bbwScentVal;
    const selectedScent = requestedScent || (rowMatchesScent(selectedWrap, activeScent) ? activeScent : rowScents(selectedWrap)[0]);
    syncScent(selectedScent);
    const tabStop = enabled(row) ? row : rows.find(item => enabled(item) && !item.closest('[hidden]'));
    rows.forEach(other => { other.tabIndex = other === tabStop ? 0 : -1; });
    const priceSource = initial && rows.length === 0
      ? root.querySelector('[data-bbw-pdp-cta-price]') : row;
    const assignedPrice = priceSource?.querySelector('.igPrice');
    const assignedCompare = priceSource?.querySelector('.igComparePrice');
    const detail = {
      productId: String(data.productId), variantId: String(data.variantId), purchaseState: data.purchaseState,
      formattedPrice: assignedPrice?.textContent.trim() || data.priceFormatted,
      formattedComparePrice: assignedCompare
        ? (assignedCompare.hidden || assignedCompare.classList.contains('ig-hidden') ? '' : assignedCompare.textContent.trim())
        : (data.compareAtPrice > data.price ? data.compareAtPriceFormatted : ''),
    };
    bindPrice(root.querySelector('[data-bbw-pdp-cta-price]'), detail, data);
    bindPrice(sticky?.querySelector('[data-bbw-sticky-price]'), detail, data);
    syncControls();
    gallery.showMedia(row.getAttribute('data-media-id'), initial);
    if (!initial) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', data.variantId);
      window.history.replaceState(window.history.state, '', url);
      root.dispatchEvent(new CustomEvent('based:variant-change', { bubbles: true, detail }));
    }
  }

  // Prefer an available row, as the current pack selector does. A scent whose rows are
  // all sold out still selects one, so the form never keeps another scent's variant.
  function chooseScent(node) {
    const choices = rows.filter(row => rowMatchesScent(row.closest(SCENT_WRAP), node.dataset.bbwScentVal));
    const pool = choices.some(enabled) ? choices.filter(enabled) : choices;
    select(pool.find(row => row.dataset.bbwPackKey === selected?.dataset.bbwPackKey) || pool[0], false, node.dataset.bbwScentVal);
  }

  function rowScents(wrap) {
    if (!wrap) return [];
    if (!wrap.dataset.bbwScentGroups) return wrap.dataset.bbwScent ? [wrap.dataset.bbwScent] : [];
    try {
      const groups = JSON.parse(wrap.dataset.bbwScentGroups);
      return Array.isArray(groups) && groups.every(group => typeof group === 'string') ? groups : [];
    } catch {
      return [];
    }
  }

  function rowMatchesScent(wrap, value) {
    if (!wrap || !value) return false;
    return rowScents(wrap).includes(value);
  }

  function radioKeys(nodes, choose) {
    nodes.forEach(node => {
      on(node, 'click', () => { if (enabled(node)) choose(node); });
      on(node, 'keydown', event => {
        const visible = nodes.filter(item => enabled(item) && !item.closest('[hidden]'));
        const index = visible.indexOf(node);
        let target;
        if (event.key === 'Home') target = visible[0];
        else if (event.key === 'End') target = visible.at(-1);
        else if (['ArrowRight', 'ArrowDown'].includes(event.key)) target = visible[(index + 1) % visible.length];
        else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) target = visible[(index + visible.length - 1) % visible.length];
        else if (event.key === ' ' && enabled(node)) target = node;
        if (!target) return;
        event.preventDefault();
        choose(target);
        target.focus({ preventScroll: true });
      });
    });
  }
  radioKeys(rows, select);
  radioKeys(scents, chooseScent);
  bindRedesignPurchasePresentation(root, { signal: abort.signal, onChange: value => { pending = value; syncControls(); } });
  bindRedesignFloatingDelegate(sticky, main, abort.signal);
  on(main, 'click', event => {
    if (pending || data?.purchaseState === 'out_of_stock') { event.preventDefault(); return; }
    if (data?.purchaseState !== 'notify_me') return;
    event.preventDefault();
    const formId = main.dataset.klaviyoFormId;
    if (formId) { window._klOnsite ||= []; window._klOnsite.push(['openForm', formId]); }
  });
  on(root, 'submit', event => {
    if (event.target !== form) return;
    if (pending || data?.purchaseState !== 'add_to_cart' || data?.directCheckoutUrl) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!pending && data?.purchaseState === 'add_to_cart' && data.directCheckoutUrl) window.location.assign(data.directCheckoutUrl);
    }
  }, { capture: true });

  if (sticky) {
    document.body.appendChild(sticky);
    let pastMain = false;
    let atFooter = false;
    const visibility = () => {
      const visible = pastMain && !atFooter;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      sticky.inert = !visible;
    };
    visibility();
    if (window.IntersectionObserver && main) {
      observe(new IntersectionObserver(entries => {
        pastMain = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
        visibility();
      }, { threshold: 0 }), main);
      const watchFooter = () => {
        const footer = Array.from(document.querySelectorAll('footer')).reverse().find(node => !node.closest('.bbw-cart, .bbw-ing-drawer, [data-bbw-sticky]'));
        if (footer) observe(new IntersectionObserver(entries => { atFooter = entries[0].isIntersecting; visibility(); }, { threshold: 0 }), footer);
      };
      if (document.readyState === 'loading') on(document, 'DOMContentLoaded', watchFooter, { once: true });
      else watchFooter();
    }
    // Watch the label's text and price, whose size follows their content (a
    // late font or price update). The button itself is sized by measureFab,
    // so watching it re-measured on every frame of its own opening animation.
    if (window.ResizeObserver) {
      const labelObserver = new ResizeObserver(measureFab);
      floating?.querySelectorAll('.bbw-fab__text, .bbw-fab__price').forEach(node => observe(labelObserver, node));
    }
    cleanup(() => {
      sticky.classList.remove('is-visible');
      sticky.setAttribute('aria-hidden', 'true');
      sticky.inert = true;
      stickyParent?.insertBefore(sticky, stickySibling?.parentNode === stickyParent ? stickySibling : null);
    });
  }
  const initial = rows.find(row => row.dataset.bbwPdpVariant === idInput?.value) || root.querySelector('[data-bbw-pdp-initial]') || rows[0];
  select(initial, true);
  if (new URLSearchParams(window.location.search).get('notify') === '1' && data?.purchaseState === 'notify_me') main?.click();
  if (document.fonts?.ready) document.fonts.ready.then(moveScentIndicator);
  on(window, 'resize', () => { clearTimeout(scentTimer); scentTimer = setTimeout(moveScentIndicator, 150); });
  cleanup(() => clearTimeout(scentTimer));
  const controller = { destroy() { abort.abort(); cleanups.forEach(callback => callback()); controllers.delete(root); } };
  controllers.set(root, controller);
  return controller;
}

export function destroyBuyBox(root) { controllers.get(root)?.destroy(); }

function sectionRoots(target) {
  return [...(target.matches?.('[data-bbw-pdp-buy]') ? [target] : []), ...target.querySelectorAll('[data-bbw-pdp-buy]')];
}
function initializePage() { sectionRoots(document).forEach(initializeBuyBox); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePage, { once: true });
else initializePage();
document.addEventListener('shopify:section:load', event => sectionRoots(event.target).forEach(initializeBuyBox));
document.addEventListener('shopify:section:unload', event => sectionRoots(event.target).forEach(destroyBuyBox));

function bindGalleryAndTips(root, { on, observe, cleanup }) {
  const mediaControls = 'product-model, model-viewer, video, iframe, deferred-media';
  var stage = root.querySelector('[data-bbw-pdp-stage]');
  var track = root.querySelector('[data-bbw-pdp-track]');
  var slides = root.querySelectorAll('[data-bbw-pdp-slide]');
  const galleryAccessibility = Array.from(slides, slide => ({
    slide, inert: slide.hasAttribute('inert'), ariaHidden: slide.getAttribute('aria-hidden'),
  }));
  function pauseSlide(slide) {
    slide?.querySelectorAll('deferred-media, product-model').forEach(media => media.pauseMedia?.());
  }
  var thumbs = root.querySelectorAll('[data-bbw-pdp-thumb]');
  var prev = root.querySelector('[data-bbw-pdp-prev]');
  var next = root.querySelector('[data-bbw-pdp-next]');
  var index = 0;
  var dissolveTimer;
  var dragPointerId = null;
  var lbPointerId = null;
  function withinTapDistance(event, start) {
    return Math.abs(event.clientX - start.clientX) < 8 && Math.abs(event.clientY - start.clientY) < 8;
  }
  function beginDrag(track, property) {
    const left = track.getBoundingClientRect().left;
    track.classList.add('is-dragging');
    track.style.setProperty(property, '0px');
    const offset = left - track.getBoundingClientRect().left;
    track.style.setProperty(property, offset + 'px');
    return offset;
  }
  function releaseCapture(target, pointerId) {
    if (pointerId == null || !target?.releasePointerCapture) return;
    if (target.hasPointerCapture && !target.hasPointerCapture(pointerId)) return;
    try { target.releasePointerCapture(pointerId); } catch { }
  }
  function preserveDragStyle(element, property) {
    const value = element?.style.getPropertyValue(property);
    const priority = element?.style.getPropertyPriority(property);
    const wasDragging = element?.classList.contains('is-dragging');
    return () => {
      if (!element) return;
      if (value) element.style.setProperty(property, value, priority);
      else element.style.removeProperty(property);
      element.classList.toggle('is-dragging', wasDragging);
    };
  }
  const restoreGalleryDrag = preserveDragStyle(track, '--pdp-drag');

  function syncSlides(track, property, slides, index, prev, next) {
    const active = document.activeElement;
    track?.style.setProperty(property, index);
    slides.forEach((slide, i) => {
      slide.inert = i !== index;
      slide.toggleAttribute('inert', i !== index);
      slide.setAttribute('aria-hidden', String(i !== index));
    });
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= slides.length - 1;
    if (active === prev && prev?.disabled) next?.focus({ preventScroll: true });
    if (active === next && next?.disabled) prev?.focus({ preventScroll: true });
  }

  function sync() {
    syncSlides(track, '--pdp-i', slides, index, prev, next);
    thumbs.forEach(function (thumb, i) { thumb.setAttribute('aria-current', i === index ? 'true' : 'false'); });
  }

  function show(target, immediate = false) {
    if (!slides.length || !track) return;
    target = Math.max(0, Math.min(slides.length - 1, target));
    clearTimeout(dissolveTimer);
    if (target !== index) pauseSlide(slides[index]);
    if (immediate) {
      const transition = track.style.getPropertyValue('transition');
      const priority = track.style.getPropertyPriority('transition');
      track.style.setProperty('transition', 'none');
      track.classList.remove('is-dissolving');
      index = target;
      sync();
      void track.offsetWidth;
      if (transition) track.style.setProperty('transition', transition, priority);
      else track.style.removeProperty('transition');
      return;
    }
    if (target === index) {
      track.classList.remove('is-dissolving');
      return;
    }

    if (Math.abs(target - index) > 2 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      track.classList.add('is-dissolving');
      dissolveTimer = setTimeout(function () {
        index = target;
        sync();
        void track.offsetWidth;
        track.classList.remove('is-dissolving');
      }, 170);
      return;
    }

    track.classList.remove('is-dissolving');
    index = target;
    sync();
  }

  function showMedia(id, immediate = false) {
    if (!id) return;
    for (var i = 0; i < slides.length; i += 1) {
      if (slides[i].getAttribute('data-media-id') === id) {
        show(i, immediate);
        return;
      }
    }
  }

  thumbs.forEach(function (thumb, i) { on(thumb, 'click', function () { show(i); }); });
  if (prev) on(prev, 'click', function () { show(index - 1); });
  if (next) on(next, 'click', function () { show(index + 1); });
  sync();


  if (stage) {
    var idleMq = window.matchMedia('(max-width: 767px)');
    var idleTimer = null;

    var wake = function () {
      window.clearTimeout(idleTimer);
      stage.classList.remove('is-idle');

      if (idleMq.matches) {
        idleTimer = window.setTimeout(function () { stage.classList.add('is-idle'); }, 2500);
      }
    };

    ['pointerdown', 'touchstart', 'click', 'focusin'].forEach(function (evt) {
      on(stage, evt, wake, { passive: true });
    });


    if (idleMq.addEventListener) on(idleMq, 'change', wake);

    wake();
  }

  if (stage && track) {
    on(stage, 'keydown', function (event) {
      if (event.target.closest('summary, ' + mediaControls)) return;
      if (event.key === 'ArrowLeft') { show(index - 1); event.preventDefault(); }
      else if (event.key === 'ArrowRight') { show(index + 1); event.preventDefault(); }
      else if (event.key === 'Home') { show(0); event.preventDefault(); }
      else if (event.key === 'End') { show(slides.length - 1); event.preventDefault(); }
    });

    var dragStart = null;
    var dragOffset = 0;
    var dx = 0;
    var moved = false;
    var width = 1;

    var endDrag = function (cancelled = false) {
      releaseCapture(stage, dragPointerId);
      dragPointerId = null;
      if (!dragStart) return;
      dragStart = null;
      track.classList.remove('is-dragging');
      track.style.setProperty('--pdp-drag', '0px');
      if (!cancelled && Math.abs(dx) > Math.max(40, width * 0.1)) show(index + (dx < 0 ? 1 : -1));
      dx = 0;
    };

    on(stage, 'pointerdown', function (event) {
      if (dragStart) return;
      if (event.target.closest('button, summary, product-model, model-viewer, video, iframe, deferred-media')) return;
      dragStart = event;
      moved = false;
      dx = 0;
      width = stage.getBoundingClientRect().width || 1;
      dragOffset = beginDrag(track, '--pdp-drag');
      dragPointerId = event.pointerId;
      if (stage.setPointerCapture) stage.setPointerCapture(event.pointerId);
    });

    on(stage, 'pointermove', function (event) {
      if (!dragStart || event.pointerId !== dragPointerId) return;
      dx = event.clientX - dragStart.clientX;
      moved ||= !withinTapDistance(event, dragStart);
      if ((index === 0 && dx > 0) || (index === slides.length - 1 && dx < 0)) dx *= 0.32;
      track.style.setProperty('--pdp-drag', dragOffset + dx + 'px');
    });

    on(stage, 'pointerup', function (event) {
      if (event.pointerId !== dragPointerId) return;

      var wasTap = dragStart && !moved && withinTapDistance(event, dragStart);
      endDrag();
      if (wasTap) openLightbox(index);
    });
    on(stage, 'pointercancel', event => { if (event.pointerId === dragPointerId) endDrag(true); });
  }


  var lightbox = root.querySelector('[data-bbw-pdp-lightbox]');
  var lbTrack = lightbox ? lightbox.querySelector('[data-bbw-lb-track]') : null;
  const restoreLightboxDrag = preserveDragStyle(lbTrack, '--lb-drag');
  var lbSlides = lightbox ? lightbox.querySelectorAll('[data-bbw-lb-slide]') : [];
  const slideAccessibility = Array.from(lbSlides, slide => ({
    slide, inert: slide.hasAttribute('inert'), ariaHidden: slide.getAttribute('aria-hidden'),
  }));
  var lbCounter = lightbox ? lightbox.querySelector('[data-bbw-lb-counter]') : null;
  var lbPrev = lightbox ? lightbox.querySelector('[data-bbw-lb-prev]') : null;
  var lbNext = lightbox ? lightbox.querySelector('[data-bbw-lb-next]') : null;
  var lbIndex = 0;
  var lbReturnFocus = null;
  var bodyStyles = null;
  var lightboxParent = lightbox?.parentNode;
  var lightboxSibling = lightbox?.nextSibling;

  var lbMq = window.matchMedia('(max-width: 767px)');

  function lbSync() {
    syncSlides(lbTrack, '--lb-i', lbSlides, lbIndex, lbPrev, lbNext);
    if (lbCounter) lbCounter.textContent = (lbIndex + 1) + ' / ' + lbSlides.length;
  }

  function lbShow(target) {
    target = Math.max(0, Math.min(lbSlides.length - 1, target));
    if (target !== lbIndex) pauseSlide(lbSlides[lbIndex]);
    lbIndex = target;
    lbSync();
  }

  function openLightbox(at) {
    if (!lightbox || !lbSlides.length || !lbMq.matches) return;
    if (lightbox.classList.contains('is-open')) return;
    pauseSlide(slides[index]);
    lbReturnFocus = document.activeElement;
    bodyStyles = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
    lbIndex = Math.max(0, Math.min(lbSlides.length - 1, at || 0));
    if (lightbox.hidden) {
      lightbox.hidden = false;
      void lightbox.offsetWidth;
    }

    document.body.style.paddingRight = (window.innerWidth - document.documentElement.clientWidth) + 'px';
    document.body.style.overflow = 'hidden';
    lbSync();
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    var close = lightbox.querySelector('[data-bbw-lb-close]');
    if (close) close.focus({ preventScroll: true });
  }

  if (lightbox && lbSlides.length) {

    document.body.appendChild(lightbox);

    var closeLightbox = function (syncGallery = true) {
      if (!lightbox.classList.contains('is-open')) return;
      pauseSlide(lbSlides[lbIndex]);
      lbEnd?.(true);
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = bodyStyles?.overflow || '';
      document.body.style.paddingRight = bodyStyles?.paddingRight || '';
      bodyStyles = null;

      if (syncGallery !== false) show(lbIndex);
      if (lbReturnFocus && lbReturnFocus.focus) lbReturnFocus.focus({ preventScroll: true });
    };

    on(lightbox.querySelector('[data-bbw-lb-close]'), 'click', closeLightbox);


    var lbMqChange = function () { if (!lbMq.matches) closeLightbox(); };
    if (lbMq.addEventListener) on(lbMq, 'change', lbMqChange);

    if (lbPrev) on(lbPrev, 'click', function () { lbShow(lbIndex - 1); });
    if (lbNext) on(lbNext, 'click', function () { lbShow(lbIndex + 1); });

    var expand = root.querySelector('[data-bbw-pdp-expand]');
    if (expand) on(expand, 'click', function () { openLightbox(index); });

    on(document, 'keydown', function (event) {
      if (!lightbox.classList.contains('is-open')) return;
      if (event.key === 'Tab') {
        const focusable = Array.from(lightbox.querySelectorAll('button, a[href], input, select, textarea, video[controls], audio[controls], iframe, [tabindex], [contenteditable="true"]')).filter(node => {
          const slide = node.closest('[data-bbw-lb-slide]');
          if (node.disabled || node.tabIndex < 0 || (slide && slide !== lbSlides[lbIndex])) return false;
          if (node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
          for (let ancestor = node; ancestor && ancestor !== lightbox; ancestor = ancestor.parentElement) {
            const style = getComputedStyle(ancestor);
            if (style.display === 'none' || style.visibility === 'hidden') return false;
          }
          return true;
        });
        const current = focusable.indexOf(document.activeElement);
        if (current < 0 || (event.shiftKey ? current === 0 : current === focusable.length - 1)) {
          const target = event.shiftKey ? focusable.at(-1) : focusable[0];
          target?.focus({ preventScroll: true });
          event.preventDefault();
        }
      }
      else if (event.key === 'Escape') { closeLightbox(); event.preventDefault(); }
      else if (event.target.closest?.(mediaControls)) return;
      else if (event.key === 'ArrowLeft') { lbShow(lbIndex - 1); event.preventDefault(); }
      else if (event.key === 'ArrowRight') { lbShow(lbIndex + 1); event.preventDefault(); }
      else if (event.key === 'Home') { lbShow(0); event.preventDefault(); }
      else if (event.key === 'End') { lbShow(lbSlides.length - 1); event.preventDefault(); }
    });


    var lbDragStart = null;
    var lbDragOffset = 0;
    var lbDx = 0;
    var lbMoved = false;
    var lbDownTarget = null;
    var lbEnd = function (cancelled = false) {
      releaseCapture(lightbox, lbPointerId);
      lbPointerId = null;
      if (!lbDragStart) return;
      lbDragStart = null;
      lbTrack.classList.remove('is-dragging');
      lbTrack.style.setProperty('--lb-drag', '0px');
      if (!cancelled && Math.abs(lbDx) > 48) lbShow(lbIndex + (lbDx < 0 ? 1 : -1));
      lbDx = 0;
    };
    on(lightbox, 'pointerdown', function (event) {
      if (lbDragStart) return;
      if (event.target.closest('button, product-model, model-viewer, video, iframe, deferred-media')) return;

      lbDownTarget = event.target;
      lbDragStart = event;
      lbMoved = false;
      lbDx = 0;
      lbDragOffset = beginDrag(lbTrack, '--lb-drag');
      lbPointerId = event.pointerId;
      if (lightbox.setPointerCapture) lightbox.setPointerCapture(event.pointerId);
    });
    on(lightbox, 'pointermove', function (event) {
      if (!lbDragStart || event.pointerId !== lbPointerId) return;
      lbDx = event.clientX - lbDragStart.clientX;
      lbMoved ||= !withinTapDistance(event, lbDragStart);
      if ((lbIndex === 0 && lbDx > 0) || (lbIndex === lbSlides.length - 1 && lbDx < 0)) lbDx *= 0.32;
      lbTrack.style.setProperty('--lb-drag', lbDragOffset + lbDx + 'px');
    });
    on(lightbox, 'pointerup', function (event) {
      if (event.pointerId !== lbPointerId) return;

      var wasTap = lbDragStart && !lbMoved && withinTapDistance(event, lbDragStart);
      lbEnd();

      if (wasTap && lbDownTarget && lbDownTarget.tagName !== 'IMG') closeLightbox();
      lbDownTarget = null;
    });
    on(lightbox, 'pointercancel', event => {
      if (event.pointerId !== lbPointerId) return;
      lbEnd(true);
      lbDownTarget = null;
    });

    on(lightbox, 'dragstart', function (event) { event.preventDefault(); });
  }


  const tips = root.querySelectorAll('.bbw-pdp-buy__inside');
  function placeTips() {
    tips.forEach(tip => {
      var wrap = tip.parentElement;
      var trigger = wrap.querySelector('.bbw-pdp-buy__variant-inside');
      if (!trigger) return;
      var wrapBox = wrap.getBoundingClientRect();
      var triggerBox = trigger.getBoundingClientRect();
      tip.style.setProperty('--tip-bottom', Math.round(wrapBox.bottom - triggerBox.top + 8) + 'px');
    });
  }

  placeTips();
  if (window.ResizeObserver) {
    observe(new ResizeObserver(placeTips), root);
  }


  function closeTips() {
    tips.forEach(tip => {
      tip.hidden = true;
      tip.parentElement.classList.remove('is-tip-open');
    });
  }

  tips.forEach(tip => {
    var wrap = tip.parentElement;
    var trigger = wrap.querySelector('.bbw-pdp-buy__variant-inside');
    if (!trigger) return;
    const resetTip = event => {
      if (!wrap.contains(event.relatedTarget)) tip.hidden = false;
    };
    on(wrap, 'pointerenter', resetTip);
    on(wrap, 'focusin', resetTip);

    on(trigger, 'click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      var wasOpen = wrap.classList.contains('is-tip-open');
      closeTips();
      if (!wasOpen) { tip.hidden = false; wrap.classList.add('is-tip-open'); }
    });

    on(tip.querySelector('[data-bbw-tip-close]'), 'click', function () {
      trigger.closest('button')?.focus({ preventScroll: true });
      closeTips();
    });
    on(tip, 'click', function (event) { event.stopPropagation(); });
  });

  on(document, 'click', closeTips);
  on(document, 'keydown', function (event) {
    if (event.key === 'Escape') closeTips();
  });

  cleanup(() => {
    slides.forEach(pauseSlide);
    lbSlides.forEach(pauseSlide);
    endDrag?.(true);
    lbEnd?.(true);
    dragStart = lbDragStart = null;
    dx = dragOffset = lbDx = lbDragOffset = 0;
    lbDownTarget = null;
    if (closeLightbox) closeLightbox(false);
    clearTimeout(dissolveTimer);
    clearTimeout(idleTimer);
    stage?.classList.remove('is-idle');
    track?.classList.remove('is-dissolving');
    restoreGalleryDrag();
    restoreLightboxDrag();
    closeTips();
    if (lightbox && lightboxParent) {
      lightbox.hidden = true;
      lightboxParent.insertBefore(lightbox, lightboxSibling?.parentNode === lightboxParent ? lightboxSibling : null);
    }
    [...galleryAccessibility, ...slideAccessibility].forEach(({ slide, inert, ariaHidden }) => {
      slide.inert = inert;
      slide.toggleAttribute('inert', inert);
      if (ariaHidden === null) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', ariaHidden);
    });
  });
  return { showMedia };
}
